/**
 * STORYBOARD UI - Scenes Module
 * Handles scene rendering, navigation, actions, and CRUD
 *
 * Extracted from ui.js as part of Phase 2 refactoring
 */

import {
  getStoryboard,
  setStoryboard,
  getActiveStoryboard,
  getActiveSceneIndex,
  setActiveSceneIndex,
  setActiveIdeaId,
  getActiveIdeaId,
  loadFromIdea,
  getGenerationStatus,
  updateSceneField,
  getFrameStatus,
  setSceneFrame,
  saveToDatabase,
  isDatabaseIdea,
  hasPanelPool,
  hasSourceImageChanged,
  duplicateScene,
  deleteScene,
  setSceneVideo,
  insertScene
} from '../state.js';
import { detectSceneCountFromText } from '../api.js';
// XSS prevention
import { escapeHtml, escapeAttr, sanitizeUrl } from '../../../../js/core/sanitize.js';
// Video generation status check (Task 113: Fix placeholder disappearing on scene switch)
import { isVideoGenerating } from '../../features/video-generation.js';
// Icons for consistent button styling
import { Icons } from '../../../../js/shared/icons.js';

// ============================================================================
// MODULE STATE
// ============================================================================

// Callback for rendering storyboard (set by main ui.js)
let renderStoryboardCallback = null;
// Callback for updating video tab count (from videos module)
let updateVideoTabCountCallback = null;
// Callback for rendering videos tab (from videos module)
let renderVideosTabCallback = null;
// Callback for auto-importing panels (from panels module)
let autoImportPanelsCallback = null;

/**
 * Set the callback for rendering storyboard
 * Called by main ui.js during initialization
 */
export function setRenderStoryboardCallback(callback) {
  renderStoryboardCallback = callback;
}

/**
 * Set the callback for updating video tab count
 */
export function setUpdateVideoTabCountCallback(callback) {
  updateVideoTabCountCallback = callback;
}

/**
 * Set the callback for rendering videos tab
 */
export function setRenderVideosTabCallback(callback) {
  renderVideosTabCallback = callback;
}

/**
 * Set the callback for auto-importing panels
 */
export function setAutoImportPanelsCallback(callback) {
  autoImportPanelsCallback = callback;
}

// ============================================================================
// SKELETON UI (Placeholder before generation)
// ============================================================================

/**
 * Render skeleton placeholder UI
 */
export function renderSkeletonUI() {
  return `
    <div class="storyboard-skeleton">
      <!-- Skeleton Header -->
      <div class="skeleton-header">
        <div class="skeleton-bar skeleton-style"></div>
        <div class="skeleton-bar skeleton-duration"></div>
      </div>

      <!-- Skeleton Scene Tabs -->
      <div class="skeleton-tabs">
        <div class="skeleton-tab active">
          <div class="skeleton-bar"></div>
          <div class="skeleton-bar small"></div>
        </div>
        <div class="skeleton-tab">
          <div class="skeleton-bar"></div>
          <div class="skeleton-bar small"></div>
        </div>
        <div class="skeleton-tab">
          <div class="skeleton-bar"></div>
          <div class="skeleton-bar small"></div>
        </div>
      </div>

      <!-- Skeleton Scene Card - Two Column -->
      <div class="skeleton-scene-card">
        <div class="skeleton-scene-header">
          <div class="skeleton-bar"></div>
        </div>
        <div class="skeleton-scene-body">
          <!-- Left: Frames -->
          <div class="skeleton-frames">
            <div class="skeleton-frame">
              <div class="skeleton-frame-label"></div>
              <div class="skeleton-frame-content"></div>
            </div>
            <div class="skeleton-frame">
              <div class="skeleton-frame-label"></div>
              <div class="skeleton-frame-content"></div>
            </div>
          </div>
          <!-- Right: Details -->
          <div class="skeleton-details">
            <div class="skeleton-field">
              <div class="skeleton-bar label"></div>
              <div class="skeleton-bar value"></div>
              <div class="skeleton-bar value short"></div>
            </div>
            <div class="skeleton-field">
              <div class="skeleton-bar label"></div>
              <div class="skeleton-bar value"></div>
            </div>
            <div class="skeleton-section">
              <div class="skeleton-bar section-header"></div>
              <div class="skeleton-bar value"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Call to Action -->
      <div class="skeleton-cta">
        <p class="skeleton-cta-text">Click <strong>GENERATE SCENES WITH AI</strong> below to create video prompts</p>
      </div>
    </div>
  `;
}

// ============================================================================
// STORYBOARD RENDERING
// ============================================================================

/**
 * Render the complete storyboard for an idea
 * Supports both canvas (inline) and modal container IDs
 */
export function renderStoryboard(ideaId, idea = null) {
  // Try canvas IDs first, then modal IDs
  const container = document.getElementById('canvas-video-scenes-container')
    || document.getElementById('video-scenes-container');
  const placeholder = document.getElementById('canvas-video-scenes-placeholder')
    || document.getElementById('video-scenes-placeholder');

  if (!container) return;

  // Set active idea
  setActiveIdeaId(ideaId);

  // Only load from idea meta if we DON'T already have a storyboard in state
  // This prevents overwriting freshly generated storyboards with old data from idea.meta
  const existingStoryboard = getStoryboard(ideaId);
  if (!existingStoryboard && idea) {
    loadFromIdea(idea);
  }

  // Get storyboard data (may be from state or just loaded)
  const storyboard = getStoryboard(ideaId);
  const status = getGenerationStatus(ideaId);

  // Hide the old placeholder
  if (placeholder) placeholder.style.display = 'none';

  // Clear existing content
  container.innerHTML = '';

  if (status === 'generating') {
    // Show loading state with detected scene count
    const ideaText = idea?.text || idea?.visual || '';
    const detectedCount = detectSceneCountFromText(ideaText, idea);
    container.innerHTML = renderLoadingState(detectedCount);
    return;
  }

  if (!storyboard || !storyboard.scenes?.length) {
    // Show skeleton placeholder
    container.innerHTML = renderSkeletonUI();
    return;
  }

  // Check for storyboard image and panel status
  const currentImageUrl = ideaId ? window.getIdeaImageUrl?.(ideaId) : null;
  const hasPanels = hasPanelPool(ideaId);

  if (currentImageUrl) {
    // Check if image has changed since last split
    const imageChanged = hasSourceImageChanged(ideaId, currentImageUrl);

    // Check if any scene frames are already assigned (user may have edited them)
    const hasSceneFrames = storyboard?.scenes?.some(s =>
      s.frames?.start?.url || s.frames?.end?.url
    );

    if (!hasPanels && !hasSceneFrames) {
      // Only auto-import if we have no panels AND no frames assigned
      // This prevents re-splitting when user has already worked on frames
      if (autoImportPanelsCallback) {
        autoImportPanelsCallback(ideaId, currentImageUrl);
      }
    } else if (imageChanged && hasPanels) {
      // Image regenerated but we have existing panels - DON'T auto-resplit
      // User can manually re-split from Panel Library if they want
      console.log('[Storyboard] Source image changed - re-split in Panel Library if needed');
    }
  }

  // Render full storyboard
  let html = '';

  // Scene navigation tabs
  html += renderSceneNavTabs(storyboard);

  // Active scene card
  const activeIndex = getActiveSceneIndex();
  const activeScene = storyboard.scenes[activeIndex];
  if (activeScene) {
    html += renderSceneCard(activeScene, storyboard, activeIndex);
  }

  container.innerHTML = html;
}

/**
 * Render loading state
 * @param {number|null} sceneCount - Detected scene count (optional)
 */
function renderLoadingState(sceneCount = null) {
  const countText = sceneCount ? `${sceneCount} ` : '';
  return `
    <div class="storyboard-loading">
      <div class="loading-spinner"></div>
      <p class="loading-text">Generating ${countText}production-ready scenes...</p>
      <p class="loading-subtext">Expanding your shots for video AI</p>
    </div>
  `;
}

/**
 * Render storyboard header with editable style
 */
function renderStoryboardHeader(storyboard) {
  const style = storyboard.style?.visual || 'Cinematic commercial';
  const duration = storyboard.duration || ':30';

  return `
    <div class="storyboard-header">
      <div class="storyboard-meta storyboard-style-field">
        <label class="storyboard-meta-label" for="storyboard-style-input">STYLE:</label>
        <input type="text"
               id="storyboard-style-input"
               class="storyboard-style-input"
               data-field="style"
               value="${escapeAttr(style)}" />
      </div>
      <div class="storyboard-meta">
        <span class="storyboard-meta-label">DURATION:</span>
        <span class="storyboard-duration">${escapeHtml(duration)}</span>
      </div>
    </div>
  `;
}

/**
 * Render scene navigation tabs with ADD SCENE button
 */
function renderSceneNavTabs(storyboard) {
  const activeIndex = getActiveSceneIndex();

  const tabs = storyboard.scenes.map((scene, index) => {
    const isActive = index === activeIndex;

    return `
      <button type="button"
              class="scene-nav-tab ${isActive ? 'active' : ''}"
              data-action="select-scene"
              data-scene="${escapeAttr(scene.seq)}">
        <span class="scene-nav-number">SCENE ${escapeHtml(scene.seq)}</span>
        <span class="scene-nav-timing">${escapeHtml(scene.timing)}</span>
      </button>
    `;
  }).join('');

  return `
    <div class="scene-nav-tabs-container">
      <div class="scene-nav-tabs">
        ${tabs}
      </div>
      <button type="button"
              class="btn-add-scene"
              data-action="add-scene"
              title="Add new scene">
        +
      </button>
    </div>
  `;
}

/**
 * Render a single scene card (full two-column layout)
 */
export function renderSceneCard(scene, storyboard, index) {
  const style = storyboard.style?.visual || 'Cinematic commercial';
  const totalScenes = storyboard.scenes.length;

  // Get camera and audio as text
  const cameraText = formatCameraText(scene.camera);
  const audioText = formatAudioText(scene.audio);

  // Debug logging
  console.log(`[Storyboard] Rendering scene ${scene.seq}, audio type:`, typeof scene.audio, 'value:', scene.audio);
  console.log(`[Storyboard] Formatted audioText:`, audioText);

  // Build the FULL prompt that will be copied (includes camera, audio, voiceover, duration)
  let fullPrompt = scene.visualPrompt || `${style}. ${scene.visual}`;
  if (cameraText) fullPrompt += `\n\nCamera: ${cameraText}`;
  if (audioText) fullPrompt += `\nAudio: ${audioText}`;
  const voiceoverText = scene.voiceover || '';
  if (voiceoverText && voiceoverText.toLowerCase() !== 'null') {
    fullPrompt += `\nVoiceover: "${voiceoverText}"`;
  }
  fullPrompt += `\nDuration: ~8 seconds\nAspect Ratio: 16:9`;

  return `
    <div class="scene-card-screenplay" data-scene="${escapeAttr(scene.seq)}">
      <!-- Scene Header -->
      <div class="scene-card-header-sp">
        <span class="scene-badge">SCENE ${escapeHtml(scene.seq)} OF ${escapeHtml(totalScenes)}</span>
        <span class="scene-timing-badge">${escapeHtml(scene.timing)}</span>
        <button type="button"
                class="btn-icon-xs btn-delete-scene"
                data-action="delete-scene"
                data-scene="${escapeAttr(scene.seq)}"
                title="Delete Scene"
                ${totalScenes <= 1 ? 'disabled' : ''}>
          ✕
        </button>
      </div>

      <!-- Scene Body - Two Column Layout -->
      <div class="scene-card-body-full">
        <!-- Left Column: Frames -->
        <div class="scene-frames-column">
          <!-- Video Generation Section: PRIORITY ORDER MATTERS (Task 113) -->
          <!-- Check generating FIRST so regeneration shows placeholder over old video -->
          ${isVideoGenerating(storyboard.ideaId || storyboard.sourceIdea?.id, {
                type: 'scene',
                sceneSeq: scene.seq
              }) ? `
            <!-- STATE 1: GENERATING (Priority - covers both new and regen) -->
            <div class="video-gen-section">
              <div class="scene-video-generating">
                <div class="pixel-loader-sm"></div>
                <span class="generating-text">RENDERING VIDEO...</span>
                <span class="generating-subtext">1-2 minutes</span>
              </div>
            </div>
          ` : scene.video?.url ? `
            <!-- STATE 2: VIDEO EXISTS (Show player with regen option) -->
            <div class="scene-video-player">
              <video src="${sanitizeUrl(scene.video.url)}" controls preload="metadata"></video>
              <div class="scene-video-actions">
                <button type="button" class="btn-scene-video-regen"
                        data-action="regen-scene-video" data-scene="${escapeAttr(scene.seq)}">
                  ↻ MAKE NEW VIDEO
                </button>
              </div>
            </div>
          ` : `
            <!-- STATE 3: IDLE (No video, not generating - show Make Video button) -->
            <div class="video-gen-section">
              <button type="button"
                      class="btn-gen-video ${scene.frames?.start?.url ? '' : 'disabled'}"
                      data-action="gen-video"
                      data-scene="${escapeAttr(scene.seq)}"
                      ${scene.frames?.start?.url ? '' : 'disabled'}>
                <span class="btn-icon">▶</span>
                MAKE VIDEO
              </button>
              ${!scene.frames?.start?.url ? `
                <p class="video-gen-note">Generate START frame first</p>
              ` : ''}
            </div>
          `}

          ${renderFrameSlot(scene, 'start', 'START FRAME')}
          ${renderFrameSlot(scene, 'end', 'END FRAME')}
        </div>

        <!-- Right Column: Editable Details -->
        <div class="scene-details-column">
          <!-- Visual Description (Editable) -->
          <div class="scene-field">
            <label class="scene-field-label">VISUAL</label>
            <textarea class="scene-field-input scene-visual-input"
                      data-field="visual"
                      data-scene="${escapeAttr(scene.seq)}"
                      rows="5">${escapeHtml(scene.visual || '')}</textarea>
          </div>

          <!-- Camera (Editable) -->
          <div class="scene-field">
            <label class="scene-field-label">CAMERA</label>
            <textarea class="scene-field-input scene-camera-input"
                      data-field="camera"
                      data-scene="${escapeAttr(scene.seq)}"
                      rows="2"
                      placeholder="Shot type, movement, angle...">${escapeHtml(cameraText)}</textarea>
          </div>

          <!-- Voiceover (Editable) - Spoken narration -->
          <div class="scene-field">
            <label class="scene-field-label">VOICEOVER</label>
            <textarea class="scene-field-input scene-voiceover-input"
                      data-field="voiceover"
                      data-scene="${escapeAttr(scene.seq)}"
                      rows="2"
                      placeholder="Spoken narration (or leave empty for none)...">${escapeHtml((scene.voiceover && scene.voiceover.toLowerCase() !== 'null') ? scene.voiceover : '')}</textarea>
          </div>

          <!-- Audio (Editable) - Music/SFX -->
          <div class="scene-field">
            <label class="scene-field-label">AUDIO</label>
            <textarea class="scene-field-input scene-audio-input"
                      data-field="audio"
                      data-scene="${escapeAttr(scene.seq)}"
                      rows="2"
                      placeholder="SFX and music direction...">${escapeHtml(audioText)}</textarea>
          </div>

          <!-- Action Buttons Row -->
          <div class="scene-actions-row">
            <button type="button"
                    class="btn-copy-prompt"
                    data-action="copy-prompt"
                    data-scene="${escapeAttr(scene.seq)}">
              <span>📋 COPY</span>
            </button>
            <button type="button"
                    class="btn-save-scene"
                    data-action="save-scene"
                    data-scene="${escapeAttr(scene.seq)}"
                    disabled>
              💾 SAVE
            </button>
          </div>
        </div>
      </div>

      <!-- Full Prompt (Expandable) -->
      <div class="scene-prompt-section">
        <div class="scene-prompt-header" data-action="expand-prompt">
          <span class="scene-prompt-label">FULL PROMPT</span>
          <span class="expand-icon">▼</span>
        </div>
        <div class="scene-prompt-content collapsed">
          <textarea class="scene-prompt-textarea" readonly>${escapeHtml(fullPrompt)}</textarea>
        </div>
      </div>

      <!-- Navigation -->
      <div class="scene-navigation">
        <button type="button" class="btn-scene-nav" data-action="prev-scene" ${index === 0 ? 'disabled' : ''}>
          ◄ PREV
        </button>
        <span class="scene-nav-indicator">${escapeHtml(index + 1)} / ${escapeHtml(totalScenes)}</span>
        <button type="button" class="btn-scene-nav" data-action="next-scene" ${index === totalScenes - 1 ? 'disabled' : ''}>
          NEXT ►
        </button>
      </div>
    </div>
  `;
}

/**
 * Render frame slot (start or end) with panel picker and drag/drop support
 */
export function renderFrameSlot(scene, type, label) {
  const frameData = scene.frames?.[type];
  const frameStatus = getFrameStatus(scene.seq, type);
  const isGenerating = frameStatus === 'generating';

  // Check if we have a panel pool
  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;
  const hasPool = hasPanelPool(ideaId);

  // Action buttons (always visible below image) - uses btn-img-action for consistency with idea canvas
  const frameActions = `
    <div class="frame-actions-bar">
      <button type="button" class="btn-img-action"
              data-action="preview-frame" data-scene="${escapeAttr(scene.seq)}" data-type="${escapeAttr(type)}"
              title="Preview">${Icons.fullscreen16({ size: 14 })}</button>
      ${hasPool ? `
        <button type="button" class="btn-img-action"
                data-action="swap-panel" data-scene="${escapeAttr(scene.seq)}" data-type="${escapeAttr(type)}"
                title="Swap Panel">${Icons.storyboardGrid({ size: 14 })}</button>
      ` : ''}
      <button type="button" class="btn-img-action"
              data-action="gen-${escapeAttr(type)}-frame" data-scene="${escapeAttr(scene.seq)}"
              title="Generate">${Icons.regenerate({ size: 14 })}</button>
      <button type="button" class="btn-img-action"
              data-action="edit-frame" data-scene="${escapeAttr(scene.seq)}" data-type="${escapeAttr(type)}"
              title="Edit">${Icons.editFilled({ size: 14 })}</button>
      <button type="button" class="btn-img-action"
              data-action="download-frame" data-scene="${escapeAttr(scene.seq)}" data-type="${escapeAttr(type)}"
              title="Download">${Icons.download16({ size: 14 })}</button>
      <label class="btn-img-action btn-upload-label" title="Upload">
        ${Icons.plus({ size: 14 })}
        <input type="file" accept="image/*" hidden
               class="frame-upload-input"
               data-scene="${escapeAttr(scene.seq)}" data-type="${escapeAttr(type)}">
      </label>
    </div>
  `;

  if (frameData?.url) {
    // Has image - show with actions below
    // frame-content is clickable for preview
    return `
      <div class="frame-slot has-image" data-type="${escapeAttr(type)}" data-scene="${escapeAttr(scene.seq)}">
        <div class="frame-label">${escapeHtml(label)}</div>
        <div class="frame-content frame-clickable"
             data-action="preview-frame" data-scene="${escapeAttr(scene.seq)}" data-type="${escapeAttr(type)}">
          <img src="${sanitizeUrl(frameData.url)}" alt="${escapeAttr(label)}" class="frame-image" />
        </div>
        ${frameActions}
      </div>
    `;
  }

  // Empty state with drag zone
  return `
    <div class="frame-slot empty drop-zone" data-type="${escapeAttr(type)}" data-scene="${escapeAttr(scene.seq)}">
      <div class="frame-label">${escapeHtml(label)}</div>
      <div class="frame-content frame-placeholder">
        ${isGenerating ? `
          <div class="frame-generating">
            <div class="frame-spinner"></div>
            <span>Generating...</span>
          </div>
        ` : `
          <div class="frame-empty-actions">
            ${hasPool ? `
              <button type="button" class="btn-frame-pick"
                      data-action="swap-panel" data-scene="${escapeAttr(scene.seq)}" data-type="${escapeAttr(type)}">
                ▦ FROM STORYBOARD
              </button>
            ` : ''}
            <button type="button" class="btn-frame-gen"
                    data-action="gen-${escapeAttr(type)}-frame" data-scene="${escapeAttr(scene.seq)}">
              + GENERATE
            </button>
          </div>
          <p class="frame-drop-hint">or drop image here</p>
        `}
      </div>
    </div>
  `;
}

/**
 * Format camera object/string to text
 */
export function formatCameraText(camera) {
  if (!camera) return '';
  if (typeof camera === 'string') return camera;
  const parts = [camera.shot, camera.movement, camera.angle].filter(Boolean);
  return parts.join(' · ');
}

/**
 * Format audio to text for display
 * Audio is now normalized to string in state, but handle objects for backwards compat
 */
export function formatAudioText(audio) {
  if (!audio) return '';

  // String audio - return as is (this is the expected format now)
  if (typeof audio === 'string') return audio;

  // Object format (legacy) - convert to string
  if (typeof audio === 'object') {
    const parts = [];
    if (audio.voiceover) parts.push(`VO: "${audio.voiceover}"`);
    if (audio.sfx) parts.push(`SFX: ${audio.sfx}`);
    if (audio.music) parts.push(`Music: ${audio.music}`);
    return parts.join(' | ') || '';
  }

  return '';
}

// ============================================================================
// SCENE EVENT HANDLERS
// ============================================================================

/**
 * Select a scene by index
 */
export function selectScene(index) {
  console.log('[Storyboard] Selecting scene:', index);
  setActiveSceneIndex(index);

  const storyboard = getActiveStoryboard();
  if (storyboard) {
    const ideaId = storyboard.ideaId || storyboard.sourceIdea?.id;
    if (ideaId) {
      renderStoryboard(ideaId);
    }
  }
}

/**
 * Navigate to prev/next scene
 */
export function navigateScene(delta) {
  const storyboard = getActiveStoryboard();
  if (!storyboard?.scenes) return;

  const currentIndex = getActiveSceneIndex();
  const newIndex = currentIndex + delta;

  if (newIndex >= 0 && newIndex < storyboard.scenes.length) {
    selectScene(newIndex);
  }
}

/**
 * Copy scene prompt to clipboard (includes camera AND audio)
 */
export function copyScenePrompt(sceneSeq, button) {
  const storyboard = getActiveStoryboard();
  if (!storyboard?.scenes) return;

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene) return;

  const style = storyboard.style?.visual || 'Cinematic commercial';

  // Build complete prompt with all details
  let fullPrompt = scene.visualPrompt || `${style}. ${scene.visual}`;

  // Add camera if present
  const cameraText = formatCameraText(scene.camera);
  if (cameraText) {
    fullPrompt += `\n\nCamera: ${cameraText}`;
  }

  // Add audio if present
  const audioText = formatAudioText(scene.audio);
  if (audioText) {
    fullPrompt += `\nAudio: ${audioText}`;
  }

  fullPrompt += `\nDuration: ~8 seconds\nAspect Ratio: 16:9`;

  // Copy to clipboard with visual feedback
  navigator.clipboard.writeText(fullPrompt).then(() => {
    console.log('[Storyboard] Copied scene', sceneSeq, 'prompt');
    // Visual feedback
    const span = button.querySelector('span');
    if (span) {
      const originalText = span.textContent;
      span.textContent = '✓ COPIED!';
      button.classList.add('copied');
      setTimeout(() => {
        span.textContent = originalText;
        button.classList.remove('copied');
      }, 2000);
    }
  }).catch(err => {
    console.error('[Storyboard] Copy failed:', err);
  });
}

/**
 * Toggle prompt expansion
 */
export function togglePromptExpansion(target) {
  const section = target.closest('.scene-prompt-section');
  const content = section?.querySelector('.scene-prompt-content');
  const icon = section?.querySelector('.expand-icon');

  if (!content) return;

  const isCollapsed = content.classList.contains('collapsed');
  content.classList.toggle('collapsed', !isCollapsed);
  if (icon) icon.textContent = isCollapsed ? '▲' : '▼';
}

/**
 * Save scene changes with immediate UI feedback (Task 113)
 * Prevents double-clicks by immediately disabling button and showing loading state
 */
export async function saveSceneChanges(sceneSeq, button) {
  const sceneCard = button.closest('.scene-card-screenplay');
  if (!sceneCard) return;

  // 1. IMMEDIATE UI FEEDBACK - Prevent double-clicks
  const originalText = button.innerHTML;
  button.disabled = true;
  button.classList.add('loading');
  button.textContent = 'SAVING...';

  // Get the active storyboard to find the idea ID
  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;

  // Collect all field values
  const fields = sceneCard.querySelectorAll('[data-field]');
  fields.forEach(field => {
    updateSceneField(sceneSeq, field.dataset.field, field.value);
  });

  // Save to database if this is a saved idea
  let savedToDb = false;
  if (ideaId && isDatabaseIdea(ideaId)) {
    try {
      await saveToDatabase(ideaId);
      savedToDb = true;
      console.log('[Storyboard] Scene', sceneSeq, 'saved to database');
    } catch (error) {
      console.error('[Storyboard] Failed to save scene to database:', error);
      // Show error state briefly
      button.classList.remove('loading');
      button.textContent = '⚠ ERROR';
      button.classList.add('error');
      setTimeout(() => {
        button.classList.remove('error');
        button.disabled = false;
        button.innerHTML = originalText;
      }, 2000);
      return; // Exit early on error
    }
  }

  // 2. SUCCESS STATE
  sceneCard.classList.remove('has-changes');
  button.classList.remove('loading', 'active');
  button.innerHTML = savedToDb ? '✓ SAVED' : '✓ SAVED (local)';

  // 3. RESET AFTER DELAY (keep disabled until next change triggers input event)
  setTimeout(() => {
    button.innerHTML = originalText;
    // Don't re-enable - wait for next 'input' event in handleStoryboardInput
  }, 1500);

  console.log('[Storyboard] Scene', sceneSeq, 'saved', savedToDb ? 'to DB' : 'locally');
}

/**
 * Generate frame image - Opens image generation modal
 */
export function generateFrame(sceneSeq, type, button) {
  const storyboard = getActiveStoryboard();
  if (!storyboard?.scenes) return;

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene) return;

  const ideaId = storyboard.ideaId || storyboard.sourceIdea?.id;
  if (!ideaId) return;

  // Get the visual prompt for this scene
  const visualPrompt = scene.visual || scene.setting || 'Scene visual';

  // Open the image generation modal for this scene frame
  if (window.openImageGenModalForSceneFrame) {
    window.openImageGenModalForSceneFrame(ideaId, sceneSeq, type, visualPrompt);
  } else {
    console.error('[Storyboard] openImageGenModalForSceneFrame not available');
    alert('Image generation module not loaded. Please refresh the page.');
  }
}

/**
 * Open video generation modal for a specific scene
 * @param {number} sceneSeq - Scene sequence number
 */
export function handleGenerateSceneVideo(sceneSeq) {
  const storyboard = getActiveStoryboard();
  if (!storyboard) {
    console.error('[Storyboard] No active storyboard');
    return;
  }

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene) {
    console.error('[Storyboard] Scene not found:', sceneSeq);
    return;
  }

  // Validate start frame exists
  if (!scene.frames?.start?.url) {
    alert('Generate a START frame first before making a video.');
    return;
  }

  const ideaId = storyboard.ideaId || storyboard.sourceIdea?.id;

  // Format camera text
  const cameraText = formatCameraText(scene.camera);

  // Open video modal with scene context
  if (window.openVideoGenModal) {
    window.openVideoGenModal(ideaId, {
      type: 'scene',
      sceneSeq: sceneSeq,
      startFrameUrl: scene.frames.start.url,
      endFrameUrl: scene.frames.end?.url || null,
      visual: scene.visual || scene.visualPrompt,
      camera: cameraText,
      audio: scene.audio
    });
  } else {
    console.error('[Storyboard] openVideoGenModal not available');
    alert('Video generation module not loaded. Please refresh the page.');
  }
}

/**
 * Preview a scene video in fullscreen modal
 * @param {number} sceneSeq - Scene sequence number
 */
export function handlePreviewSceneVideo(sceneSeq) {
  const storyboard = getActiveStoryboard();
  if (!storyboard) return;

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene?.video?.url) return;

  // Use the video preview modal from video-generation module
  if (window.openVideoPreview) {
    window.openVideoPreview(scene.video.url);
  } else {
    // Fallback: open in new tab
    window.open(scene.video.url, '_blank');
  }
}

/**
 * Handle scene video generation completion event
 * @param {CustomEvent} event
 */
export function handleSceneVideoComplete(event) {
  const { ideaId, sceneSeq, videoUrl, meta } = event.detail;

  console.log(`[Storyboard] Scene ${sceneSeq} video complete:`, videoUrl);

  // Update state
  setSceneVideo(sceneSeq, videoUrl, meta?.provider || 'veo', meta);

  // Re-render the scene card to show video player
  refreshSceneCard(sceneSeq);

  // Update the VIDEOS tab count badge
  if (updateVideoTabCountCallback) {
    updateVideoTabCountCallback();
  }

  // Refresh videos tab if it's currently active
  const videosTab = document.querySelector('.prompt-tab[data-target="tab-videos"]');
  const videosContent = document.getElementById('tab-videos');
  if (videosTab && videosContent?.classList.contains('active')) {
    if (renderVideosTabCallback) {
      renderVideosTabCallback();
    }
  }
}

/**
 * Refresh a single scene card without full re-render
 * @param {number} sceneSeq - Scene sequence number
 */
export function refreshSceneCard(sceneSeq) {
  const storyboard = getActiveStoryboard();
  if (!storyboard) return;

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene) return;

  const index = storyboard.scenes.findIndex(s => s.seq === sceneSeq);
  const cardEl = document.querySelector(`.scene-card-screenplay[data-scene="${sceneSeq}"]`);

  if (cardEl) {
    // Replace with updated card
    cardEl.outerHTML = renderSceneCard(scene, storyboard, index);
  }
}

// ============================================================================
// SCENE CRUD HANDLERS
// ============================================================================

/**
 * Handle duplicate scene action
 * @param {number} sceneSeq - Scene sequence number to duplicate
 */
export function handleDuplicateScene(sceneSeq) {
  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;

  if (!ideaId || !sceneSeq) {
    console.warn('[Storyboard] Cannot duplicate: missing ideaId or sceneSeq');
    return;
  }

  // Call the state function to duplicate
  const newScene = duplicateScene(ideaId, sceneSeq);

  if (newScene) {
    // Set active to the NEW scene (it's now at position sceneSeq + 1)
    setActiveSceneIndex(sceneSeq); // 0-based, so sceneSeq points to new scene

    // Re-render the storyboard
    renderStoryboard(ideaId);

    console.log(`[Storyboard UI] Duplicated scene ${sceneSeq}`);
  }
}

/**
 * Handle delete scene action
 * @param {number} sceneSeq - Scene sequence number to delete
 */
export function handleDeleteScene(sceneSeq) {
  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;

  if (!ideaId || !sceneSeq) {
    console.warn('[Storyboard] Cannot delete: missing ideaId or sceneSeq');
    return;
  }

  // Check if this is the last scene
  if (storyboard.scenes?.length <= 1) {
    alert('Cannot delete the last scene. Storyboards must have at least one scene.');
    return;
  }

  // Confirm deletion
  if (!confirm(`Delete Scene ${sceneSeq}? This cannot be undone.`)) {
    return;
  }

  // Call the state function to delete
  const deleted = deleteScene(ideaId, sceneSeq);

  if (deleted) {
    // Adjust active index if needed
    const currentIndex = getActiveSceneIndex();
    const newSceneCount = storyboard.scenes.length - 1; // After delete

    if (currentIndex >= newSceneCount) {
      setActiveSceneIndex(Math.max(0, newSceneCount - 1));
    } else if (currentIndex >= sceneSeq - 1) {
      // If we deleted a scene before or at current, adjust down
      setActiveSceneIndex(Math.max(0, currentIndex - 1));
    }

    // Re-render the storyboard
    renderStoryboard(ideaId);

    console.log(`[Storyboard UI] Deleted scene ${sceneSeq}`);
  }
}

/**
 * Handle edit frame action - opens Image Edit modal with scene context
 * @param {HTMLElement} target - The button element that was clicked
 */
export async function handleEditFrame(target) {
  const sceneSeq = parseInt(target.dataset.scene);
  const frameType = target.dataset.type;

  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;

  if (!ideaId) {
    console.warn('[Storyboard] Cannot edit frame: missing ideaId');
    return;
  }

  // Check if frame has an image
  const scene = storyboard.scenes?.find(s => s.seq === sceneSeq);
  if (!scene?.frames?.[frameType]?.url) {
    alert('No image in this frame to edit. Generate or upload an image first.');
    return;
  }

  // Call the global openEditModal with scene context
  // Note: Overlay is NOT shown here - it's shown when user actually submits the edit
  if (typeof window.openEditModal === 'function') {
    await window.openEditModal(ideaId, {
      type: 'scene',
      sceneSeq: sceneSeq,
      frame: frameType
    });
    console.log(`[Storyboard] Opened edit modal for Scene ${sceneSeq} ${frameType} frame`);
  } else {
    console.error('[Storyboard] Image Edit module not loaded');
    alert('Edit feature not available. Please refresh the page.');
  }
}

/**
 * Show editing overlay on a scene frame
 * @param {number} sceneSeq
 * @param {string} frameType - 'start' or 'end'
 */
export function showFrameEditingOverlay(sceneSeq, frameType) {
  const frameEl = document.querySelector(
    `.frame-slot[data-scene="${sceneSeq}"][data-type="${frameType}"] .frame-content`
  );
  if (!frameEl) return;

  // Add editing class
  frameEl.classList.add('editing');

  // Add overlay if not already present
  if (!frameEl.querySelector('.frame-editing-overlay')) {
    frameEl.insertAdjacentHTML('beforeend', `
      <div class="frame-editing-overlay">
        <div class="pixel-loader"></div>
        <span class="editing-text">EDITING...</span>
      </div>
    `);
  }
}

/**
 * Hide editing overlay on a scene frame
 * @param {number} sceneSeq
 * @param {string} frameType - 'start' or 'end'
 */
export function hideFrameEditingOverlay(sceneSeq, frameType) {
  const frameEl = document.querySelector(
    `.frame-slot[data-scene="${sceneSeq}"][data-type="${frameType}"] .frame-content`
  );
  if (!frameEl) return;

  frameEl.classList.remove('editing');
  const overlay = frameEl.querySelector('.frame-editing-overlay');
  if (overlay) overlay.remove();
}

// ============================================================================
// AUTO-SAVE ON BLUR
// ============================================================================

let autoSaveInitialized = false;

/**
 * Initialize auto-save for scene fields
 * Automatically saves scene changes when user navigates away from a field
 */
export function initSceneAutoSave() {
  if (autoSaveInitialized) return;
  autoSaveInitialized = true;

  document.addEventListener('blur', async (e) => {
    // Check if it's a scene field input
    if (!e.target.matches('.scene-field-input, .scene-visual-input, .storyboard-style-input')) return;

    const sceneAttr = e.target.dataset.scene;
    const field = e.target.dataset.field;
    const value = e.target.value;

    // Handle storyboard-level style field
    if (field === 'style' && !sceneAttr) {
      const storyboard = getActiveStoryboard();
      const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;
      if (ideaId && isDatabaseIdea(ideaId)) {
        try {
          await saveToDatabase(ideaId);
          console.log('[Storyboard] Auto-saved storyboard style');
        } catch (error) {
          console.error('[Storyboard] Auto-save style failed:', error);
        }
      }
      return;
    }

    const sceneSeq = parseInt(sceneAttr);
    if (!sceneSeq || !field) return;

    // Update in-memory state
    updateSceneField(sceneSeq, field, value);

    // Save to database if this is a saved idea
    const storyboard = getActiveStoryboard();
    const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;

    if (ideaId && isDatabaseIdea(ideaId)) {
      try {
        await saveToDatabase(ideaId);
        console.log(`[Storyboard] Auto-saved scene ${sceneSeq} field: ${field}`);

        // Update save button state
        const saveBtn = document.querySelector(`.btn-save-scene[data-scene="${sceneSeq}"]`);
        if (saveBtn) {
          saveBtn.textContent = '✓ SAVED';
          saveBtn.disabled = true;
          setTimeout(() => {
            saveBtn.textContent = '💾 SAVE';
          }, 1500);
        }
      } catch (error) {
        console.error('[Storyboard] Auto-save failed:', error);
      }
    }
  }, true); // Use capture phase for reliable detection

  console.log('[Storyboard] Auto-save initialized');
}

// ============================================================================
// ADD SCENE HANDLER
// ============================================================================

/**
 * Handle add scene action
 * Adds a new blank scene at the end of the storyboard
 */
export function handleAddScene() {
  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;

  if (!ideaId) {
    console.warn('[Storyboard] Cannot add scene: no active idea');
    return;
  }

  // Insert at end (after last scene)
  const lastSceneSeq = storyboard.scenes.length;
  const newScene = insertScene(ideaId, lastSceneSeq);

  if (newScene) {
    // Activate the new scene (it's now at the end)
    const newIndex = storyboard.scenes.length; // Note: scenes array was modified by insertScene
    setActiveSceneIndex(newIndex - 1);

    // Re-render the storyboard
    renderStoryboard(ideaId);

    console.log('[Storyboard UI] Added new scene at position', lastSceneSeq + 1);
  }
}
